/**
 * DayCell - Calendar day cell component for no-spend calendar
 */
import { useState } from "react";
import { Minus, Edit3 } from "lucide-react";
import { getAccentColors } from "../../utils/themes";
import { usePurple } from "../../utils/purple";
import { usePlan } from "../../utils/plan"; // plan_10

const DayCell = ({ day, isNoSpend, isNeutral, isSpend, isCurrentDay, isFuture, hasNote, onClick, onAddNote, theme = 'pink' }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [showNoteBtn, setShowNoteBtn] = useState(false);
  // Purple (admin-only): lavender days with no shadows.
  const purple = usePurple();
  const plan = usePlan(); // plan_10

  // Get theme-aware accent colors
  const accentColors = getAccentColors(theme);

  // For warm theme, use brighter green for no-spend calendar markers
  const isWarm = theme === 'warm';
  const noSpendGreen = isWarm ? '#3D8B5A' : accentColors.green;
  const noSpendGreenDark = isWarm ? '#2D6B45' : accentColors.greenDark;

  const handleClick = () => {
    if (isFuture) return;
    setIsAnimating(true);
    onClick();
    setTimeout(() => setIsAnimating(false), 300);
  };

  // Determine the background style based on day type
  const getBgStyle = () => {
    // plan_10: Money plan - soft pastel circles, faded future days.
    if (plan) {
      if (isFuture) return { className: 'cursor-not-allowed font-semibold', style: { background: 'transparent', color: '#D2CDDD' } };
      if (isNoSpend) return { className: 'font-semibold', style: { background: '#DFF2E7', color: '#2F8756' } };
      if (isNeutral) return { className: 'font-semibold', style: { background: '#FCF3D2', color: '#7C7350' } };
      if (isSpend) return { className: 'font-semibold', style: { background: '#FAE4ED', color: '#C04B7C' } };
      return { className: 'font-semibold', style: { background: 'transparent', color: '#26252E' } };
    }
    if (isFuture) return { className: 'opacity-30 cursor-not-allowed bg-gray-50 text-gray-300' };
    // Purple (admin-only), option A: one lavender family. No-spend days are
    // the strongest colour, neutral mid lavender, spent the faintest.
    if (purple) {
      if (isNoSpend) return { className: 'font-bold', style: { background: '#8B5CE0', color: '#FFFFFF' } };
      if (isNeutral) return { className: 'font-bold', style: { background: '#E4D9F9', color: '#5E35A8' } };
      if (isSpend) return { className: 'font-semibold', style: { background: '#F6F1FC', color: '#8E7BAE' } };
      return { className: 'font-medium', style: { background: '#FFFFFF', color: '#5D5370', boxShadow: 'inset 0 0 0 1px #EEE6F6' } };
    }
    if (isNoSpend) return { 
      className: 'text-white font-bold shadow-lg',
      style: { background: `linear-gradient(to bottom right, ${noSpendGreen}, ${noSpendGreenDark})` }
    };
    if (isNeutral) return { className: 'text-gray-800 font-medium', style: { background: '#FDD835', boxShadow: '0 4px 6px rgba(253,216,53,0.4)' } };
    if (isSpend) {
      if (theme === 'blue') return { className: 'text-white font-medium shadow-sm', style: { background: 'linear-gradient(to bottom right, #6366f1, #4f46e5)' } };
      if (theme === 'warm') return { className: 'text-white font-medium shadow-sm', style: { background: 'linear-gradient(to bottom right, #7C5C3A, #5C3D20)' } };
      return { className: 'text-white font-medium shadow-sm', style: { background: 'linear-gradient(to bottom right, #E91E8C, #C71874)' } };
    }
    return { className: 'theme-bg-50 text-gray-700 font-medium hover:theme-bg-100' };
  };

  const bgConfig = getBgStyle();

  return (
    <div className="relative" onMouseEnter={() => setShowNoteBtn(true)} onMouseLeave={() => setShowNoteBtn(false)}>
      <button
        data-testid={`day-cell-${day}`}
        onClick={handleClick}
        disabled={isFuture}
        className={`
          ${plan ? 'w-10 h-10 text-[14.5px]' : 'w-12 h-12 md:w-14 md:h-14'} flex items-center justify-center rounded-full
          transition-all duration-300 cursor-pointer relative font-medium
          ${isAnimating ? 'day-pop' : ''}
          ${!isFuture ? 'hover:scale-110' : ''}
          ${bgConfig.className}
          ${isCurrentDay && !plan ? 'ring-2 ring-theme-500 ring-offset-2' : ''}
        `}
        style={plan && isCurrentDay ? { ...bgConfig.style, boxShadow: 'inset 0 0 0 2px #B9A6F0', color: bgConfig.style.background === 'transparent' ? '#7A5DD3' : bgConfig.style.color } : bgConfig.style}
      >
        <span className="relative z-10">{day}</span>

      </button>
      {!isFuture && !isNoSpend && !isNeutral && showNoteBtn && (
        <button
          onClick={(e) => { e.stopPropagation(); onAddNote(); }}
          className="absolute -bottom-1 -right-1 w-5 h-5 theme-bg-500 rounded-full flex items-center justify-center text-white shadow-md hover:theme-bg-600 transition-colors z-10"
        >
          <Edit3 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

export default DayCell;
